import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const serviceAreas = [
  { id: 'jacksonville', name: 'Jacksonville', nearby: ['St. Augustine', 'Orange Park', 'Ponte Vedra'] },
  { id: 'orlando', name: 'Orlando', nearby: ['Kissimmee', 'Winter Park', 'Sanford'] },
  { id: 'tampa', name: 'Tampa', nearby: ['St. Petersburg', 'Clearwater', 'Brandon'] },
  { id: 'tallahassee', name: 'Tallahassee', nearby: ['Quincy', 'Crawfordville', 'Monticello'] },
  { id: 'fort-myers', name: 'Fort Myers', nearby: ['Cape Coral', 'Naples', 'Bonita Springs'] },
  { id: 'west-palm-beach', name: 'West Palm Beach', nearby: ['Boca Raton', 'Delray Beach', 'Jupiter'] },
  { id: 'miami', name: 'Miami', nearby: ['Hialeah', 'Miami Beach', 'Coral Gables'] },
];

const servicesList = [
  'Fiber Optic Installation',
  'CCTV Systems', 
  'Structured Cabling', 
  'Access Control', 
  'Networking Solutions', 
  'Data Centers',
  'Site Survey'
];

export default function ServiceAreas() {
  const { language } = useLanguage();
  const isEs = language === 'es';

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <section className="py-12" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 51, 102, 0.867) 50%, rgba(0, 166, 81, 0.25) 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              {isEs ? 'Entregando Soluciones de Infraestructura en toda Florida' : 'Delivering Infrastructure Solutions Across Florida'}
            </h1>
            <p className="text-white/80 text-sm max-w-2xl mx-auto">
              {isEs 
                ? 'Brindamos servicios integrales de cableado estructurado y telecomunicaciones en todo el estado de Florida'
                : 'We provide comprehensive structured cabling and telecommunications services throughout the state of Florida'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Map Section - Like reference image */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Map and Info */}
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Florida Map - Using the reference image */}
              <div className="bg-white rounded-lg shadow-lg p-4">
                <img 
                  src="/florida-map-coverage.png" 
                  alt="IQES Low Voltage Solutions - Service Coverage Across Florida"
                  className="w-full h-auto"
                />
              </div>

              {/* Services Info */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800">{isEs ? 'Servicios en Florida' : 'Services in Florida'}</h2>
                
                <Card className="bg-white border border-gray-200 shadow-sm">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-gray-800 mb-3">{isEs ? 'Todos Nuestros Servicios:' : 'All Our Services:'}</h3>
                    <ul className="space-y-2">
                      {servicesList.map((service, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4" style={{ color: '#00A651' }} />
                          <span className="text-gray-700">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="text-white" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 166, 81, 0.5) 100%)' }}>
                  <CardContent className="p-5">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div><div className="text-2xl font-bold">13+</div><div className="text-xs text-white/70">{isEs ? 'Años' : 'Years'}</div></div>
                      <div><div className="text-2xl font-bold">280+</div><div className="text-xs text-white/70">{isEs ? 'Proyectos' : 'Projects'}</div></div>
                      <div><div className="text-2xl font-bold">7</div><div className="text-xs text-white/70">{isEs ? 'Ciudades' : 'Cities'}</div></div>
                      <div><div className="text-2xl font-bold">100%</div><div className="text-xs text-white/70">{isEs ? 'Satisfacción' : 'Satisfaction'}</div></div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-orange-50 border border-orange-200">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-gray-800 mb-2">{isEs ? '¿Proyecto en mente?' : 'Have a project?'}</h3>
                    <p className="text-sm text-gray-600 mb-4">{isEs ? 'Cotización' : 'Quote'}</p>
                    <div className="flex flex-wrap gap-2">
                      <Link to="/quote"><Button className="bg-orange-500 hover:bg-orange-600 text-white text-sm">{isEs ? 'Cotización' : 'Quote'}</Button></Link>
                      <a href="tel:+17869813760"><Button variant="outline" className="border-orange-500 text-orange-600 text-sm"><Phone className="w-4 h-4 mr-1" />786-981-3760</Button></a>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* City Cards with "y Áreas Cercanas" */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-bold text-center mb-8" style={{ color: 'rgb(0, 51, 102)' }}>{isEs ? 'Áreas de Servicio' : 'Service Areas'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {serviceAreas.map((area, index) => (
              <motion.div key={area.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }}>
                <Card className="bg-white border border-gray-200 hover:shadow-lg hover:border-[#00A651] transition-all">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full ${area.color} flex items-center justify-center flex-shrink-0`}>
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">{area.name}</h3>
                        <p className="text-xs text-orange-500 font-medium mb-2">
                          {isEs ? 'y Áreas Cercanas' : 'and Nearby Areas'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {area.nearby.join(', ')}
                        </p>
                        <Link to="/quote" className="text-xs text-blue-600 hover:underline mt-2 inline-block">
                          {isEs ? 'Proyectos →' : 'Projects →'}
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 166, 81, 0.25) 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-2">{isEs ? '¿No ve su ciudad?' : "Don't see your city?"}</h2>
          <p className="text-white/70 text-sm mb-4">{isEs ? 'Servimos toda Florida' : 'We serve all of Florida'}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="tel:+17869813760"><Button variant="outline" className="border-white text-white hover:bg-white/10 text-sm"><Phone className="w-4 h-4 mr-1" />786-981-3760</Button></a>
            <Link to="/quote"><Button className="bg-orange-500 hover:bg-orange-600 text-white text-sm">{isEs ? 'Cotización' : 'Quote'}<ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
